import { autoinject, bindable, computedFrom, observable, Container } from 'aurelia-framework';
import { DataItemOption, ReportedServiceDataItemService } from '../customers/reported-service-data-item-service';
import { AttachedDataItem, ReportedServiceDataItemType } from '../reported-service-data-items/reported-service-data-item-models';
import { IDroppableHandler } from '../../components/drag-and-drop/models';
import { last } from 'lodash';
import {ScopeType} from "../scopes/scope-type-service";

@autoinject
export class ReportedDataItemsForm  implements IDroppableHandler {
	@bindable serviceDataItems: AttachedDataItem[];
	@bindable serviceId: string;
	@bindable availableDataItems: DataItemOption[];
	@bindable availableScopeTypes: ScopeType[];
	@bindable customerId: string;
	
	adding = false;
	addingAutocompleteHasFocus = false;
	droppableHandler: IDroppableHandler;

	constructor(container: Container, private reportedServiceDataItemService: ReportedServiceDataItemService){

		this.droppableHandler = this;

		container.registerInstance(ReportedDataItemsForm, this);
	}

	@computedFrom('serviceDataItems')
	get hasItems() : boolean {
		return this.serviceDataItems && this.serviceDataItems.length > 0;
	}

	@computedFrom('customerId')
	get isCustomerSpecific() : boolean {
		return !!this.customerId;
	}
	
	showAdd() {
		this.adding = true;
		this.addingAutocompleteHasFocus = true;
	}

	get isDragged() {
		return this.serviceDataItems && this.serviceDataItems.some(item => item.isDragged === true);
	}

	onDragEnter(event: any, model: AttachedDataItem) {
		if (event.preventDefault) {
			event.preventDefault(); // Necessary. Allows us to drop.
		}

		event.dataTransfer.dropEffect = 'move';
		event.target.parentNode.className = `${event.target.parentNode.className} drag-enter`;

		return false;
	}

	onDragLeave(event: any, dragTarget: any): void {
		event.target.parentNode.className = event.target.parentNode.className.replace(' drag-enter', '');
	}

	onDrop(event, model: AttachedDataItem) {
		event.stopPropagation(); // stops the browser from redirecting.
		event.target.parentNode.className = event.target.parentNode.className.replace(' drag-enter', '');

		const jsonData = event.dataTransfer.getData('application/json');
		const data = <AttachedDataItem>JSON.parse(jsonData);
		
		if (model && model.id === data.id) {
			return false;
		}

		const item = this.serviceDataItems.find(o => o.id === data.id);

		// Remove item from old index
		this.serviceDataItems.splice(this.serviceDataItems.indexOf(item), 1);

		const newOrderNumber = model
			? model.sortOrder
			: last(this.serviceDataItems).sortOrder + 1;

		this.insertToAttachedDataItemsWithOrderNumber(item, newOrderNumber);

		return false;
	}

	insertToAttachedDataItemsWithOrderNumber(item: AttachedDataItem, orderNumber: number) {

		item.sortOrder = orderNumber;

		// Insert to the correct place in the array
		const newIndex = this.serviceDataItems.findIndex(r => r.sortOrder >= orderNumber);
		if (newIndex < 0) {
			// bigger than the currently biggest ordernumber -> set as last
			this.serviceDataItems.push(item);
		}
		else {
			// Increase ordernumber of each item that is after this
			for (let i = newIndex; i < this.serviceDataItems.length; i++) {
				this.serviceDataItems[i].sortOrder++;
			}
			// Insert the item to the correct index
			this.serviceDataItems.splice(newIndex, 0, item);
		}

		this.reportedServiceDataItemService.updateOrderNumbers(this.serviceId, this.customerId, this.serviceDataItems);
	}

	getUsedScopeTypesForMeasureId(measureId: string) : string[] {
		return this.serviceDataItems
			.filter(i => i.type === ReportedServiceDataItemType.Measure && i.dataItemId === measureId)
			.reduce((r, i) => r.concat(i.scopeTypeIds), []);
	}

}
